import { createContext, useContext, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Text, useTexture } from "@react-three/drei";
import * as THREE from "three";
import useCustomizerStore from "../../../state/customizer";
import useTransitionStore from "../../../state/transitions";
import { fetchCustomizerData, getSelectedWheelIndex, setSelectedColorIndex, setSelectedWheelIndex } from "../../../util/customizerData";
import { colorNamesExterior } from "../../UI/Customizer/util/colorNames";

export const MenuInteractionContext = createContext(null);
const NO_RAYCAST = () => null;

export function MenuTarget({ id, position, width, height, onActivate, selected = false, label, children }) {
  const { targets, feedback } = useContext(MenuInteractionContext);
  const mesh = useRef();
  const callback = useRef(onActivate);
  callback.current = onActivate;
  useLayoutEffect(() => {
    const target = { id, object: mesh.current, width, height, activate: () => callback.current() };
    targets.set(id, target);
    return () => { if (targets.get(id) === target) targets.delete(id); };
  }, [targets, id, width, height]);
  const hovered = feedback.hover === id;
  const pressed = feedback.pressed === id;
  return (
    <group position={position}>
      <mesh ref={mesh} userData={{ menuTarget: id }}>
        <planeGeometry args={[width, height]} />
        <meshBasicMaterial color={pressed ? "#eb0a1e" : selected ? "#687582" : hovered ? "#47535f" : "#171b20"} side={THREE.FrontSide} toneMapped={false} />
      </mesh>
      <mesh position={[0, 0, -0.001]} raycast={NO_RAYCAST}>
        <planeGeometry args={[width + 0.002, height + 0.002]} />
        <meshBasicMaterial color={selected ? "#eb0a1e" : hovered ? "#ffffff" : "#84929e"} toneMapped={false} />
      </mesh>
      {label && <Text raycast={NO_RAYCAST} position={[0, 0, 0.003]} fontSize={0.012} maxWidth={width - 0.012} textAlign="center" anchorX="center" anchorY="middle" color="#ffffff">{label}</Text>}
      {children}
    </group>
  );
}

function WheelOption({ src, label, ...props }) {
  const texture = useTexture(src);
  return (
    <MenuTarget {...props} width={0.284} height={0.045}>
      <mesh raycast={NO_RAYCAST} position={[-0.113, 0, 0.003]}>
        <circleGeometry args={[0.019, 32]} />
        <meshBasicMaterial map={texture} transparent toneMapped={false} />
      </mesh>
      <Text raycast={NO_RAYCAST} position={[-0.08, 0, 0.004]} fontSize={0.014} maxWidth={0.205} anchorX="left" color="#ffffff">{label}</Text>
    </MenuTarget>
  );
}

export default function XrMenu({ carOnly, powertrain, seeThrough, onCarOnlyChange, onPowertrainChange, onClose }) {
  const model = useCustomizerStore((state) => state.model);
  const setModel = useCustomizerStore((state) => state.setModel);
  const selection = useCustomizerStore((state) => state.selectionByModel[model]);
  const setWheelIndex = useTransitionStore((state) => state.setWheelIndex);
  const trims = useMemo(() => fetchCustomizerData(), []);
  const [trimTrayOpen, setTrimTrayOpen] = useState(false);
  const trim = trims[model];
  const colors = trim.exterior.colors;
  const wheels = trim.wheels.colors ?? [];
  const selectedColor = selection?.exterior ?? 0;
  const selectedWheel = selection?.wheel ?? 0;
  const rows = Math.ceil(colors.length / 3);
  const wheelTop = 0.04 - rows * 0.072;
  const toggleTop = wheelTop - wheels.length * 0.055 - 0.016;
  const bottom = toggleTop - 0.096;
  const top = 0.198;
  return (
    <group>
      <mesh position={[0, (top + bottom) / 2, -0.008]} raycast={NO_RAYCAST}>
        <planeGeometry args={[0.336, top - bottom]} />
        <meshBasicMaterial color="#000000" side={THREE.DoubleSide} toneMapped={false} />
      </mesh>
      <MenuTarget id="trim-toggle" position={[-0.039, 0.166, 0]} width={0.205} height={0.045} label={`${trim.label.toUpperCase()}  ${trimTrayOpen ? "HIDE TRIMS" : "TRIMS"}`} onActivate={() => setTrimTrayOpen(!trimTrayOpen)} />
      <MenuTarget id="close" position={[0.113, 0.166, 0]} width={0.055} height={0.045} label="CLOSE" onActivate={onClose} />
      <Text raycast={NO_RAYCAST} position={[0, 0.115, 0.003]} fontSize={0.012} maxWidth={0.30} textAlign="center" anchorX="center" anchorY="middle" color="#ffffff">{colorNamesExterior[selectedColor]}</Text>
      {trimTrayOpen && <group position={[-0.246, 0.134, 0.008]}>
        {trims.map((option, index) => <MenuTarget key={option.label} id={`trim-${index}`} position={[0, -index * 0.057, 0]} width={0.14} height={0.045} label={option.label.toUpperCase()} selected={index === model} onActivate={() => { setModel(index); setWheelIndex(getSelectedWheelIndex(index)); }} />)}
      </group>}
      {colors.map((color, index) => <MenuTarget key={`${model}-${index}`} id={`color-${model}-${index}`} position={[-0.104 + (index % 3) * 0.104, 0.047 - Math.floor(index / 3) * 0.072, 0]} width={0.064} height={0.056} selected={selectedColor === index} onActivate={() => setSelectedColorIndex(model, index, "exterior")}>
        <mesh position={[0, 0, 0.003]} raycast={NO_RAYCAST}>
          <circleGeometry args={[0.022, 40]} />
          <meshBasicMaterial color={Array.isArray(color) ? color[0] : color} toneMapped={false} />
        </mesh>
        {Array.isArray(color) && <mesh position={[0, 0, 0.004]} raycast={NO_RAYCAST}>
          <circleGeometry args={[0.022, 40, -Math.PI / 2, Math.PI]} />
          <meshBasicMaterial color={color[1]} toneMapped={false} />
        </mesh>}
      </MenuTarget>)}
      {wheels.map((src, index) => <WheelOption key={`${model}-${index}`} id={`wheel-${model}-${index}`} src={src} label={trim.wheels.text?.[index] ?? `Wheel ${index + 1}`} selected={index === selectedWheel} position={[0, wheelTop - index * 0.055, 0]} onActivate={() => { setSelectedWheelIndex(model, index); setWheelIndex(index); }} />)}
      <MenuTarget id="car-only" position={[0, toggleTop, 0]} width={0.284} height={0.045} selected={carOnly} label={`CAR-ONLY  ${carOnly ? "ON" : "OFF"}`} onActivate={() => onCarOnlyChange(!carOnly)} />
      <MenuTarget id="powertrain" position={[0, toggleTop - 0.055, 0]} width={0.284} height={0.045} selected={powertrain} label={`POWERTRAIN  ${powertrain ? "ON" : "OFF"}`} onActivate={() => onPowertrainChange(!powertrain)} />
      {carOnly && !seeThrough && <Text raycast={NO_RAYCAST} position={[0, bottom - 0.022, 0.003]} fontSize={0.012} maxWidth={0.30} textAlign="center" color="#ffffff">See-through unavailable in this session</Text>}
    </group>
  );
}
